import { useCallback, useRef, useState } from "react";

import { useApiCall } from "@framework/react-hooks";

import type { IStructureTreePoint } from "../types";
import { mapPointApiResponse, sortPointsByUserSortNum } from "../utils/structure-tree-utils";

export interface ITrainPointsCacheState {
  pointsByTrainId: Readonly<Record<number, IStructureTreePoint[]>>;
  loadingTrainIds: ReadonlySet<number>;
  loadTrainPoints: (trainId: number) => Promise<IStructureTreePoint[]>;
}

const MULTI_ANALYSIS_MEASURE_TYPES = [0, 1, 2, 3] as const;

export const useTrainPointsCache = (): ITrainPointsCacheState => {
  const cacheRef = useRef<Record<number, IStructureTreePoint[]>>({});
  const [pointsByTrainId, setPointsByTrainId] = useState<Record<number, IStructureTreePoint[]>>({});
  const [loadingTrainIds, setLoadingTrainIds] = useState<ReadonlySet<number>>(() => new Set());

  const { fn: fetchTrainPoints } = useApiCall(
    async (api, { trainId }: { trainId: number }) => {
      const responses = (await Promise.all(
        MULTI_ANALYSIS_MEASURE_TYPES.map(
          measureType =>
            api.fetchJson({
              url: "/point/train/autocomplete",
              method: "GET",
              data: {
                idTrain: trainId,
                measureType,
              },
            }) as Promise<unknown>,
        ),
      )) as Array<Record<string, unknown>[] | null | undefined>;

      const pointsById = new Map<number, IStructureTreePoint>();
      responses
        .flatMap(response => (Array.isArray(response) ? response : []))
        .forEach(row => {
          const point = { ...mapPointApiResponse(row), trainId };

          if (!pointsById.has(point.id)) {
            pointsById.set(point.id, point);
          }
        });

      return sortPointsByUserSortNum(Array.from(pointsById.values()));
    },
    { success: false },
  );

  const loadTrainPoints = useCallback(
    async (trainId: number): Promise<IStructureTreePoint[]> => {
      const cachedPoints = cacheRef.current[trainId];

      if (cachedPoints) {
        return cachedPoints;
      }

      setLoadingTrainIds(previous => new Set(previous).add(trainId));

      try {
        const points = await fetchTrainPoints(undefined, { trainId });

        if (!points) {
          return [];
        }

        cacheRef.current[trainId] = points;
        setPointsByTrainId(previous => ({ ...previous, [trainId]: points }));
        return points;
      } finally {
        setLoadingTrainIds(previous => {
          const next = new Set(previous);
          next.delete(trainId);
          return next;
        });
      }
    },
    [fetchTrainPoints],
  );

  return {
    pointsByTrainId,
    loadingTrainIds,
    loadTrainPoints,
  };
};
